import { useLocation } from 'react-router-dom';
import { validateEmail } from '../util/validation/formValidation';
import { TForgotPasswordPost, TVerifyForgotPasswordPost } from '../api/types';
import API from '../api/api';
import GlobalContext from './GlobalContext';
import { createContext, FC, useContext, useEffect, useState } from 'react';
import { ELDFlags, maxEmailLength } from '../util/constants';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { TResetPasswordRouterState } from './ResetPasswordContext';

export type TForgotPasswordRouterState = {
    linkExpired?: boolean;
    email?: string;
};

type TForgotPasswordContext = {
    forgotPasswordUiState: EForgotPasswordUiState;
    email: string;
    setEmail: (email: string) => void;
    emailIsValid: boolean;
    emailValidationMessage: string;
    errorMessage: string;
    errorTitle: string;
    submitForgotPasswordForm: () => Promise<void>;
    resendPasscode: () => Promise<void>;
    submitVerifyPasscode: (passcode: string) => Promise<void>;
    resetPasswordRouterState: TResetPasswordRouterState | null;
};

export enum EForgotPasswordUiState {
    default,
    expired,
    error,
    success,
}

const ForgotPasswordContext = createContext<TForgotPasswordContext | undefined>(undefined);

export const ForgotPasswordProvider: FC = ({ children }) => {
    const { gatewayAPIBaseUrl, language, subscriberId, putFullLoginLinkInLocalStorage } = useContext(GlobalContext);
    const { state: routerState }: { state: TForgotPasswordRouterState } = useLocation<TForgotPasswordRouterState>();
    const [forgotPasswordUiState, setForgotPasswordUiState] = useState(EForgotPasswordUiState.default);
    const [email, setEmail] = useState('');
    const [emailIsValid, setEmailIsValid] = useState(true);
    const [errorTitle, setErrorTitle] = useState('');
    const [emailValidationMessage, setEmailValidationMessage] = useState(language.validation.email);
    const [errorMessage, setErrorMessage] = useState(language.validation.genericError);
    const [resetPasswordRouterState, setResetPasswordRouterState] = useState<TResetPasswordRouterState | null>(null);
    const { [ELDFlags.useConsumerPasscodeVerification]: usePasscodeForVerifiication } = useFlags();

    // parse router state
    const parseRouterState = () => {
        if (routerState) {
            const { linkExpired, email } = routerState;
            linkExpired && setForgotPasswordUiState(EForgotPasswordUiState.expired);
            email && setEmail(email);
        }
    };

    // validate email
    const checkEmailIsValid = () => {
        let isValid = true;
        let validationMessage = '';
        if (!email.length) {
            isValid = false;
            validationMessage = language.validation.emailMissing;
        } else if (!validateEmail(email)) {
            isValid = false;
            validationMessage = language.validation.email;
        }
        if (email.length > maxEmailLength) {
            isValid = false;
            validationMessage = language.validation.emailMaxLength;
        }
        setEmailIsValid(isValid);
        setEmailValidationMessage(validationMessage);
        return isValid;
    };

    // sends user an email with either a passcode for verification or a link to reset password
    // depending on the feature flag
    const submitForgotPasswordForm = async () => {
        const emailIsValid = checkEmailIsValid();
        if (emailIsValid) {
            const postObject: TForgotPasswordPost = {
                email,
                ...(subscriberId && { subscriberId }),
                ...(usePasscodeForVerifiication && { usePasscode: true }),
            };
            try {
                await API.post.forgotPassword(gatewayAPIBaseUrl, postObject);
                setForgotPasswordUiState(EForgotPasswordUiState.success);
            } catch (error) {
                if (error.response?.status === 429) {
                    setErrorTitle(language.forgotPassword.error.locked);
                    setErrorMessage(language.forgotPassword.error.lockedMessage);
                } else {
                    console.error('Failed to send forgot password');
                    setErrorTitle('');
                    setErrorMessage(language.validation.genericError);
                }
                setForgotPasswordUiState(EForgotPasswordUiState.error);
            }
        }
    };

    // sends user an email with a passcode for verification
    const resendPasscode = async () => {
        const postObject: TForgotPasswordPost = {
            email,
            usePasscode: true,
            ...(subscriberId && { subscriberId }),
        };
        try {
            await API.post.forgotPassword(gatewayAPIBaseUrl, postObject);
        } catch (error) {
            console.error('Failed to resend passcode');
            throw error.response?.status;
        }
    };

    // verifies the passcode entered by the user from the email sent
    // sets auth code and userId to be used in the reset password workflow
    const submitVerifyPasscode = async (passcode: string) => {
        const postObject: TVerifyForgotPasswordPost = { code: passcode, email };
        try {
            const response = await API.post.verifyForgotPassword(postObject);
            const { code, userId } = response.data;
            setResetPasswordRouterState({ verifiedUser: { code, userId } });
        } catch (error) {
            console.error('Failed to verify passcode');
            throw error.response?.status;
        }
    };

    // get router state
    useEffect(() => {
        parseRouterState();
    }, []);

    // local storage used to retrieve login link after reset password
    useEffect(() => {
        if (forgotPasswordUiState === EForgotPasswordUiState.success) {
            putFullLoginLinkInLocalStorage(email);
        }
    }, [forgotPasswordUiState]);

    return (
        <ForgotPasswordContext.Provider
            value={{
                forgotPasswordUiState,
                email,
                emailIsValid,
                setEmail,
                emailValidationMessage,
                errorMessage,
                errorTitle,
                submitForgotPasswordForm,
                submitVerifyPasscode,
                resendPasscode,
                resetPasswordRouterState,
            }}
        >
            {children}
        </ForgotPasswordContext.Provider>
    );
};

export default ForgotPasswordContext;
